import {
    Box,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Typography,
} from '@mui/material';
import Icon from '@apigrowfinance/pbolt-icon';
import { CustomTheme, useTheme } from '@apigrowfinance/pbolt-theme-provider';
import { RiCustomerServiceLine } from 'react-icons/ri';
import { FormattedMessage } from 'react-intl';

import { IAccountHistoryItem } from '@clients/accountHistoryClient';
import { Resolution } from '@clients/operatorSessionClient';
import HistoryDatesBlock from '@components/accountHistory/accountHistoryBlock/HistoryDatesBlock';
import AccountHistoryStatusChip from '@components/chips/AccountHistoryStatusChip';
import getNameFromEmail from '@utils/getNameFromEmail';

export type AccountHistoryItemProps = {
    item: IAccountHistoryItem;
    testId?: string;
    disableGutter?: boolean;
};

const AccountHistoryItem = ({
    item,
    testId = '',
    disableGutter = false,
}: AccountHistoryItemProps) => {
    const { theme } = useTheme();
    const resolution = item?.additional_info?.resolution || null;

    return (
        <Box data-testid={testId}>
            <ListItem
                disableGutters={disableGutter}
                sx={{
                    b: theme.spacing(1),
                    bgcolor: 'background.paper',
                    mb: '2px',
                }}
                secondaryAction={
                    <ListItemText
                        primaryTypographyProps={{
                            style: {
                                textAlign:
                                    theme.direction === 'rtl'
                                        ? 'left'
                                        : 'right',
                            },
                        }}
                        primary={
                            <Box data-testid="secondary-title">
                                {resolution && (
                                    <AccountHistoryStatusChip
                                        resolution={resolution as Resolution}
                                    />
                                )}
                            </Box>
                        }
                    />
                }>
                <ListItemAvatar>
                    <Icon data-testid="avatar-icon">
                        <RiCustomerServiceLine />
                    </Icon>
                </ListItemAvatar>

                <ListItemText
                    primary={
                        <Typography
                            variant="h6"
                            data-testid="history-item-session"
                            sx={(theme: CustomTheme) => ({
                                color:
                                    theme?.custom?.brand?.textMain ||
                                    'primary.main',
                            })}>
                            <FormattedMessage
                                id="AccountHistoryItem.session"
                                defaultMessage="Session {protocol}"
                                values={{ protocol: item.protocol }}
                            />
                        </Typography>
                    }
                    secondary={
                        <span>
                            <Box
                                component="span"
                                data-testid="history-item-user">
                                {getNameFromEmail(item.user)}
                            </Box>
                            <br />
                            <Box
                                component="span"
                                data-testid="history-item-notes">
                                {item?.additional_info?.text
                                    ? item?.additional_info?.text
                                    : ''}
                            </Box>
                        </span>
                    }
                />
            </ListItem>

            <ListItem
                disableGutters
                sx={{
                    b: theme.spacing(1),
                    bgcolor: 'background.paper',
                    mb: 2,
                    p: 0,
                }}>
                <ListItemText
                    sx={{ p: 0 }}
                    secondary={
                        <HistoryDatesBlock
                            startTime={item.initial_date}
                            endTime={item?.end_date}
                        />
                    }
                />
            </ListItem>
        </Box>
    );
};

export default AccountHistoryItem;
